// src/pages/common/SearchPendingReceiptProductModalContent.tsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass } from '@/icons';
import ScrollableTable from '../../components/common/ScrollableTable';
import SearchButton from '../../components/common/SearchButton';
import SaveButton from '../../components/common/SaveButton';
import DateRangeInput from '../../components/common/DateRangeInput';
import { usePaginatedFetch } from '../../hooks/usePaginatedFetch';
import { apiGet } from '../../services/api';
import axios from 'axios';
import './SearchPendingReceiptProductModalContent.css';
import { PendingReceiptItem, ReceivedItem } from '../../types/recebimento/PendingReceiptItem';
import { showAlert } from '../../contexts/DialogContext';
import { contarSelecionados, textoSelecao, type ModoSelecao } from '@/utils/selectAll';
import { logger } from '@/utils/logger';

/**
 * Filtro da lista de pendentes a reproduzir no servidor quando `selectAll=true`.
 *
 * Espelha nome a nome o `AdicionarItensConferenciaRequestDTO.Filtro`, que por sua
 * vez espelha os @RequestParam de GET /v1/busca-inteligente/itens-pendente-recebimento.
 * Nomear diferente aqui é como o select-all e a tela divergem em silêncio.
 *
 * O eixo de categoria viaja como `codCategoria` — o mesmo nome que este modal já
 * usa no @RequestParam da listagem. Até o `api-blueltSystem#81` a listagem
 * declarava `codCategoriaProduto`, os nomes não casavam, o Spring descartava o
 * parâmetro em silêncio e a lista ignorava a categoria digitada; enquanto isso
 * durou, mandar o eixo aqui faria o servidor resolver um conjunto MAIS ESTREITO
 * do que a tela mostrou, então ele ficava fora dos dois lados.
 *
 * Com os nomes alinhados a direção do risco INVERTEU: a tela agora estreita de
 * verdade, e omitir o eixo faria o servidor resolver um conjunto MAIS LARGO do
 * que ela mostra — a divergência que a ADR-031 existe para impedir. Por isso ele
 * agora entra.
 */
export interface FiltroPendentesRecebimento {
  lojaId: number;
  requisicaoId?: number;
  dataInicio?: string;
  dataFim?: string;
  codProduto?: string;
  nomeProduto?: string;
  codCategoria?: number;
  nomeCategoria?: string;
  prioridade?: string;
  loja?: string;
  solicitante?: string;
  cnpjFornecedor?: string;
  nomeFornecedor?: string;
  ordemCompra?: number;
  comprador?: string;
}

/**
 * O que o modo 'todos' devolve ao pai: a INTENÇÃO, não a lista.
 *
 * Este modal não persiste nada — cada pai monta um payload diferente para uma
 * rota diferente (POST /{id}/itens/adicionar na conferência existente,
 * POST /itens/criar no novo recebimento). No modo enumerado ele devolve os itens
 * escolhidos; no modo 'todos' não há lista a devolver, porque quem resolve o
 * conjunto é o servidor.
 */
export interface SelecaoTudoPendentes {
  filtro: FiltroPendentesRecebimento;
  /** Ids de ordem_compra_item — a linha que a lista de pendentes exibe. */
  excludeIds: number[];
  overrides: { ordemCompraItemId: number; qtdRecebida: number }[];
  /** TETO exibido na tela, nunca uma promessa (ADR-030 item 3). */
  totalEstimado: number;
}

const AVISO_QTD_ZERO = 'Quantidade recebida deve ser maior que zero.';

// Referência estável (module-level) usada como default da prop opcional `receivedItems`.
// Evita criar novo array literal a cada render — protege a cascata useCallback do
// usePaginatedFetch (fetchFunction deps inclui receivedItems) contra loop infinito.
// Vide: paginated-fetch-pattern memory + ATENÇÃO em TableIntegrationSidePanelContent.tsx.
const EMPTY_RECEIVED_ITEMS: ReceivedItem[] = [];

interface SearchPendingReceiptProductModalContentProps {
  theme: 'dark' | 'light';
  onClose: () => void;
  tipo: string;
  lojaId: number | null;
  onItemsAdded: (items: ReceivedItem[]) => void;
  // Opcional: usado pelo fluxo "Novo Recebimento" (NewReceiptItemsSidePanelContent) para
  // filtrar client-side itens acumulados antes do POST final. No fluxo "Adicionar a Rascunho"
  // (ConferenciaRascunhoItemsSidePanelContent), o backend deduz qtdReceber via POST incremental,
  // então essa prop pode ser omitida (default []).
  receivedItems?: ReceivedItem[];
  // OPT-IN (issue #31, superfícies 8 e 9): habilita o modo "selecionar tudo entre
  // páginas". Ausente = o modal se comporta exatamente como antes, e o checkbox de
  // cabeçalho segue marcando só a página carregada. Presente = o pai recebe a
  // INTENÇÃO e a traduz para a sua rota.
  onSelecionarTudo?: (selecao: SelecaoTudoPendentes) => void;
  // Quando o pai não pode aceitar uma seleção entre páginas agora, informa o
  // motivo — o gatilho aparece desabilitado com este texto no title. Usado pelo
  // "Novo Recebimento", onde selectAll e o carrinho enumerado são excludentes.
  motivoSelecaoTudoIndisponivel?: string;
}

const SearchPendingReceiptProductModalContent: React.FC<SearchPendingReceiptProductModalContentProps> = ({
  theme,
  onClose,
  tipo,
  lojaId,
  onItemsAdded,
  receivedItems = EMPTY_RECEIVED_ITEMS,
  onSelecionarTudo,
  motivoSelecaoTudoIndisponivel,
}) => {
  // Filtros
  const [requisicao, setRequisicao] = useState('');
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [codProduto, setCodProduto] = useState('');
  const [nomeProduto, setNomeProduto] = useState('');
  const [codCategoria, setCodCategoria] = useState('');
  const [nomeCategoria, setNomeCategoria] = useState('');
  const [prioridade, setPrioridade] = useState('');
  const [loja, setLoja] = useState('');
  const [solicitante, setSolicitante] = useState('');
  const [cnpjFornecedor, setCnpjFornecedor] = useState('');
  const [nomeFornecedor, setNomeFornecedor] = useState('');
  const [ordemCompra, setOrdemCompra] = useState('');
  const [comprador, setComprador] = useState('');

  // Estado geral
  const [isLoading, setIsLoading] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState<PendingReceiptItem[]>([]);
  const [filterMode, setFilterMode] = useState<'Tudo' | 'Específico'>('Específico');
  const [quantities, setQuantities] = useState<{ [id: number]: number }>({});

  // Controle de busca
  const shouldFetchRef = useRef(false);
  const currentModeRef = useRef(filterMode);

  // --- "Selecionar tudo entre páginas" (issue #31, superfícies 8 e 9) ---------
  const [modoSelecao, setModoSelecao] = useState<ModoSelecao>('enumerado');
  const [excludeIds, setExcludeIds] = useState<Set<number>>(new Set());


  // Ref com valores atuais dos filtros (usado no fetchEspecifico)
  const filterValuesRef = useRef({
    requisicao: '',
    dataInicio: '',
    dataFim: '',
    codProduto: '',
    nomeProduto: '',
    codCategoria: '',
    nomeCategoria: '',
    prioridade: '',
    loja: '',
    solicitante: '',
    cnpjFornecedor: '',
    nomeFornecedor: '',
    ordemCompra: '',
    comprador: '',
  });

  // Filtro EFETIVAMENTE aplicado na última busca.
  //
  // `filterValuesRef` é sincronizado a cada tecla, então descreve o que está nos
  // campos AGORA — não o que gerou a lista na tela. Depois de buscar, o usuário
  // pode alterar ou limpar um campo sem clicar em Procurar: a lista continua a da
  // busca antiga. Mandar o estado corrente no `filtro` do selectAll faria o
  // servidor resolver um conjunto que o usuário nunca viu — o oposto da ADR-031.
  //
  // O mesmo snapshot alimenta a PAGINAÇÃO. Sem ele a página 2 sai com um filtro e
  // a página 1 com outro, misturando dois conjuntos na mesma tabela — e aí não
  // existe filtro nenhum capaz de reproduzir o que está na tela.
  // Espelho em ESTADO do mesmo snapshot. O ref serve à `fetchFunction`, que é um
  // useCallback cujas deps não podem oscilar (ver o EMPTY_RECEIVED_ITEMS no topo:
  // dep instável ali vira loop de paginação); o estado serve à UI, que precisa
  // re-renderizar quando a busca muda para reavaliar o gatilho. Escrita ÚNICA:
  // os dois são gravados no mesmo ponto, sempre juntos.
  const filtroAplicadoRef = useRef<typeof filterValuesRef.current | null>(null);
  const [filtroAplicado, setFiltroAplicado] = useState<typeof filterValuesRef.current | null>(null);

  // Atualiza ref dos filtros
  useEffect(() => {
    filterValuesRef.current = {
      requisicao,
      dataInicio,
      dataFim,
      codProduto,
      nomeProduto,
      codCategoria,
      nomeCategoria,
      prioridade,
      loja,
      solicitante,
      cnpjFornecedor,
      nomeFornecedor,
      ordemCompra,
      comprador,
    };
  }, [requisicao, dataInicio, dataFim, codProduto, nomeProduto, codCategoria, nomeCategoria, prioridade, loja, solicitante, cnpjFornecedor, nomeFornecedor, ordemCompra, comprador]);

  // Atualiza ref do modo atual
  useEffect(() => {
    currentModeRef.current = filterMode;
  }, [filterMode]);

  // FETCH TUDO (sem filtros adicionais)
  const fetchTudo = useCallback(
    async (pageNumber: number, _currentItems: PendingReceiptItem[]): Promise<{ items: PendingReceiptItem[]; totalItems: number }> => {
      const queryParams = new URLSearchParams({
        page: pageNumber.toString(),
        size: '10',
      });
      queryParams.append('tipo', tipo);
      queryParams.append('lojaId', lojaId?.toString() || '');

      const endpoint = `/v1/busca-inteligente/itens-pendente-recebimento?${queryParams.toString()}`;

      try {
        const res = await apiGet(endpoint);
        let items: PendingReceiptItem[] = res.data.content[0]?.itens.content.map((item: Record<string, unknown>) => ({
          ordemCompraRomaneioItemId: item.ordemCompraRomaneioItemId,
          dataCriacaoOrdemRomaneio: item.dataCriacaoOrdemRomaneio,
          status: item.status,
          usuarioId: item.usuarioId,
          nomeUsuario: item.nomeUsuario,
          codFornecedor: item.codFornecedor,
          nomeFornecedor: item.nomeFornecedor,
          lojaEntregaId: item.lojaEntregaId,
          nomeLojaEntrega: item.nomeLojaEntrega,
          qtdCompraTransferencia: item.qtdCompraTransferencia,
          qtdCompraTrnsferenciaExtra: item.qtdCompraTrnsferenciaExtra,
          qtdCompraTransferenciaOriginal: item.qtdCompraTransferenciaOriginal,
          removeOrdemCompraRomaneioItem: item.removeOrdemCompraRomaneioItem,
          requisicaoItemId: item.requisicaoItemId,
          ordemCompraRomaneioId: item.ordemCompraRomaneioId,
          codProduto: item.codProduto,
          nomeProduto: item.nomeProduto,
          codCategoriaProduto: item.codCategoriaProduto,
          nomeCategoria: item.nomeCategoria,
          requisicaoId: item.requisicaoId,
          prioridade: item.prioridade,
          nomeFantasiaLojaSoliciante: item.nomeFantasiaLojaSoliciante,
          statusRecebimento: item.statusRecebimento,
          qtdReceber: item.qtdReceber,
          qtdRecebida: item.qtdRecebida,
          qtdRecebidaTotal: item.qtdRecebidaTotal,
          ordemCompra: item.ordemCompra,
        })) || [];

        // Filtragem client-side (remove itens já salvos e ajusta quantidades)
        const savedQtdMap = receivedItems.reduce((map: { [key: number]: number }, s: ReceivedItem) => {
          const id = s.ordemCompraRomaneioItemId;
          map[id] = (map[id] || 0) + s.qtdRecebida;
          return map;
        }, {});

        items = items.filter((item) => {
          const saved = savedQtdMap[item.ordemCompraRomaneioItemId] || 0;
          item.qtdReceber -= saved;
          item.qtdRecebida = item.qtdReceber;
          item.qtdRecebidaTotal += saved;
          return item.qtdReceber > 0;
        });

        const totalItems = res.data.content[0]?.itens.page.totalElements || 0;
        return { items, totalItems };
      } catch (err) {
        logger.error('Erro (Tudo):', err);
        await showAlert(axios.isAxiosError(err) ? err.response?.data?.error || 'Erro ao buscar' : 'Erro ao buscar', { variant: 'error' });
        return { items: [], totalItems: 0 };
      }
    },
    [tipo, lojaId, receivedItems]
  );

  // FETCH ESPECÍFICO
  const fetchEspecifico = useCallback(
    async (pageNumber: number, _currentItems: PendingReceiptItem[]): Promise<{ items: PendingReceiptItem[]; totalItems: number }> => {
      // Snapshot da busca, não o estado corrente dos campos — ver filtroAplicadoRef.
      const f = filtroAplicadoRef.current ?? filterValuesRef.current;
      const hasFilter = Object.values(f).some(v => v.trim() !== '');
      if (!hasFilter) {
        await showAlert('Pelo menos um filtro deve ser preenchido.', { variant: 'warning' });
        return { items: [], totalItems: 0 };
      }

      const queryParams = new URLSearchParams({
        page: pageNumber.toString(),
        size: '10',
      });
      queryParams.append('tipo', tipo);
      queryParams.append('lojaId', lojaId?.toString() || '');
      Object.entries(f).forEach(([key, value]) => {
        if (value.trim()) {
          // Corrige o nome do parâmetro "requisicao" → "requisicaoId" TODO: Corrigir na origem
          const paramName = key === 'requisicao' ? 'requisicaoId' : key;
          queryParams.append(paramName, value.trim());
        }
      });

      const endpoint = `/v1/busca-inteligente/itens-pendente-recebimento?${queryParams.toString()}`;

      try {
        const res = await apiGet(endpoint);
        let items: PendingReceiptItem[] = res.data.content[0]?.itens.content.map((item: Record<string, unknown>) => ({
          ordemCompraRomaneioItemId: item.ordemCompraRomaneioItemId,
          dataCriacaoOrdemRomaneio: item.dataCriacaoOrdemRomaneio,
          status: item.status,
          usuarioId: item.usuarioId,
          nomeUsuario: item.nomeUsuario,
          codFornecedor: item.codFornecedor,
          nomeFornecedor: item.nomeFornecedor,
          lojaEntregaId: item.lojaEntregaId,
          nomeLojaEntrega: item.nomeLojaEntrega,
          qtdCompraTransferencia: item.qtdCompraTransferencia,
          qtdCompraTrnsferenciaExtra: item.qtdCompraTrnsferenciaExtra,
          qtdCompraTransferenciaOriginal: item.qtdCompraTransferenciaOriginal,
          removeOrdemCompraRomaneioItem: item.removeOrdemCompraRomaneioItem,
          requisicaoItemId: item.requisicaoItemId,
          ordemCompraRomaneioId: item.ordemCompraRomaneioId,
          codProduto: item.codProduto,
          nomeProduto: item.nomeProduto,
          codCategoriaProduto: item.codCategoriaProduto,
          nomeCategoria: item.nomeCategoria,
          requisicaoId: item.requisicaoId,
          prioridade: item.prioridade,
          nomeFantasiaLojaSoliciante: item.nomeFantasiaLojaSoliciante,
          statusRecebimento: item.statusRecebimento,
          qtdReceber: item.qtdReceber,
          qtdRecebida: item.qtdRecebida,
          qtdRecebidaTotal: item.qtdRecebidaTotal,
          ordemCompra: item.ordemCompra,
        })) || [];

        // Mesma filtragem client-side do fetchTudo
        const savedQtdMap = receivedItems.reduce((map: { [key: number]: number }, s: ReceivedItem) => {
          const id = s.ordemCompraRomaneioItemId;
          map[id] = (map[id] || 0) + s.qtdRecebida;
          return map;
        }, {});

        items = items.filter((item) => {
          const saved = savedQtdMap[item.ordemCompraRomaneioItemId] || 0;
          item.qtdReceber -= saved;
          item.qtdRecebida = item.qtdReceber;
          item.qtdRecebidaTotal += saved;
          return item.qtdReceber > 0;
        });

        const totalItems = res.data.content[0]?.itens.page.totalElements || 0;
        return { items, totalItems };
      } catch (err) {
        logger.error('Erro (Específico):', err);
        await showAlert(axios.isAxiosError(err) ? err.response?.data?.error || 'Erro ao buscar' : 'Erro ao buscar', { variant: 'error' });
        return { items: [], totalItems: 0 };
      }
    },
    [tipo, lojaId, receivedItems]
  );

  const fetchProducts = useCallback(
    async (pageNumber: number, currentItems: PendingReceiptItem[]) => {
      if (!shouldFetchRef.current) return { items: [], totalItems: 0 };
      return currentModeRef.current === 'Tudo'
        ? fetchTudo(pageNumber, currentItems)
        : fetchEspecifico(pageNumber, currentItems);
    },
    [fetchTudo, fetchEspecifico]
  );

  const { items, totalItems, hasMore, isLoading: fetchLoading, loadMore, reset } = usePaginatedFetch<PendingReceiptItem>({
    fetchFunction: fetchProducts,
    pageSize: 10,
    uniqueKey: 'ordemCompraRomaneioItemId',
    initialPage: 0,
  });

  const handleToggleFilterMode = () => {
    const newMode = filterMode === 'Tudo' ? 'Específico' : 'Tudo';
    setFilterMode(newMode);
    setRequisicao(''); setDataInicio(''); setDataFim(''); setCodProduto(''); setNomeProduto('');
    setCodCategoria(''); setNomeCategoria(''); setPrioridade(''); setLoja(''); setSolicitante('');
    setCnpjFornecedor(''); setNomeFornecedor(''); setOrdemCompra(''); setComprador('');
    setQuantities({}); setSelectedProducts([]); shouldFetchRef.current = false;
    // Trocar de modo descarta a busca: o filtro que resolveria o conjunto deixou
    // de existir, então a seleção entre páginas não pode sobreviver a isso.
    filtroAplicadoRef.current = null;
    setFiltroAplicado(null);
    limparSelecao();
    reset();
  };

  const handleSearch = () => {
    setIsLoading(true);
    shouldFetchRef.current = true;
    // Congela o filtro DESTA busca. Daqui em diante a paginação e o eventual
    // selectAll falam do mesmo conjunto, ainda que o usuário mexa nos campos.
    const snapshot = { ...filterValuesRef.current };
    filtroAplicadoRef.current = snapshot;
    setFiltroAplicado(snapshot);
    // Nova busca = novo conjunto. Uma seleção "todos" pendente apontaria para o
    // filtro anterior, e as exclusões seriam de itens que podem nem estar na lista.
    limparSelecao();
    reset();
    setQuantities({});
  };

  const handleSelectChange = (selected: PendingReceiptItem[]) => setSelectedProducts(selected);

  const normalizeQuantityInput = (rawValue: string) => {
    const normalized = rawValue.replace(',', '.');
    if (!normalized || normalized === '.') return 0;
    const [integerPartRaw, decimalPartRaw = ''] = normalized.split('.');
    const integerPart = integerPartRaw === '' ? '0' : integerPartRaw;
    const decimalPart = decimalPartRaw.slice(0, 3);
    const sanitized = decimalPart ? `${integerPart}.${decimalPart}` : integerPart;
    const parsed = Number.parseFloat(sanitized);
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  const handleQuantityChange = (id: number, value: string, max: number) => {
    let num = normalizeQuantityInput(value);
    if (num > 0 && num < 0.001) num = 0.001;
    if (num > max) num = max;
    num = Number(num.toFixed(3));
    setQuantities(prev => ({ ...prev, [id]: num }));
  };

  // --- Seleção: convenção de checkbox de cabeçalho de tabela ------------------
  //   vazio   -> clicar marca a fila inteira (modo 'todos');
  //   traço   -> parte selecionada; clicar limpa;
  //   marcado -> tudo selecionado; clicar limpa.

  function limparSelecao() {
    setModoSelecao('enumerado');
    setExcludeIds(new Set());
    setSelectedProducts([]);
  }

  const entrarEmTodos = () => {
    setModoSelecao('todos');
    setExcludeIds(new Set());
    setSelectedProducts([]);
  };

  const handleSelecionarItem = (item: PendingReceiptItem, marcado: boolean) => {
    const id = item.ordemCompraRomaneioItemId;
    if (modoSelecao === 'todos') {
      setExcludeIds((prev) => {
        const next = new Set(prev);
        if (marcado) next.delete(id);
        else next.add(id);
        return next;
      });
      return;
    }

    const proximos = marcado
      ? [...selectedProducts, item]
      : selectedProducts.filter((it) => it.ordemCompraRomaneioItemId !== id);

    if (marcado && items.length > 0 && proximos.length === items.length) {
      entrarEmTodos();
      return;
    }
    setSelectedProducts(proximos);
  };

  const estadoCabecalho: 'vazio' | 'parcial' | 'marcado' =
    modoSelecao === 'todos'
      ? excludeIds.size === 0
        ? 'marcado'
        : 'parcial'
      : selectedProducts.length === 0
        ? 'vazio'
        : 'parcial';

  const handleToggleCabecalho = () => {
    if (estadoCabecalho === 'vazio') entrarEmTodos();
    else limparSelecao();
  };

  // A restrição D6 do backend, avaliada sobre o filtro DA BUSCA: `selectAll` exige
  // filtro.lojaId e um dos dois filtros de fornecedor, porque a conferência só
  // aceita itens de um único par (fornecedor, loja de entrega) — regra que já
  // existe no modo enumerado (RecebimentoConferenciaItemService:911-925).
  //
  // No modo 'Tudo' do modal nenhum filtro é enviado, então o select-all ali é
  // estruturalmente impossível, não apenas indisponível.
  const temFornecedorNoFiltro = !!(
    filtroAplicado?.cnpjFornecedor.trim() || filtroAplicado?.nomeFornecedor.trim()
  );
  const podeSelecionarTudo =
    !!onSelecionarTudo &&
    !motivoSelecaoTudoIndisponivel &&
    lojaId != null &&
    filterMode === 'Específico' &&
    temFornecedorNoFiltro;

  // TETO, nunca promessa (ADR-030 item 3): `totalItems` vem do servidor e não
  // desconta o que este modal filtra no cliente (item sem saldo, item já no
  // carrinho do pai), nem o que a materialização do servidor ainda vai descartar.
  const qtdSelecionada = contarSelecionados(
    modoSelecao,
    totalItems,
    modoSelecao === 'enumerado' ? selectedProducts.length : excludeIds.size,
  );

  const temSelecao =
    modoSelecao === 'todos' ? qtdSelecionada > 0 : selectedProducts.length > 0;

  // Guarda de coerência: se o select-all deixar de ser possível com o modo 'todos'
  // ligado, a prop some da tabela (o cabeçalho volta ao comportamento histórico)
  // mas o modo continuaria valendo — e o Salvar dispararia uma seleção que a tela
  // não oferece mais. Os caminhos conhecidos (trocar Tudo/Específico, nova busca)
  // já limpam na mão; este efeito cobre os que vêm do PAI, como o carrinho do
  // "Novo Recebimento" deixar de estar vazio.
  useEffect(() => {
    if (!podeSelecionarTudo && modoSelecao === 'todos') limparSelecao();
  }, [podeSelecionarTudo, modoSelecao]);

  /**
   * Traduz o snapshot da busca para o `filtro` do contrato.
   *
   * Só campos preenchidos entram: um campo em branco não é um predicado, e
   * mandá-lo vazio criaria superfície para divergir da listagem. `codCategoria`
   * entra desde o `api-blueltSystem#81` — ver FiltroPendentesRecebimento.
   */
  const montarFiltro = (): FiltroPendentesRecebimento => {
    const f = filtroAplicadoRef.current ?? filterValuesRef.current;
    const texto = (v: string) => (v.trim() ? v.trim() : undefined);
    const numero = (v: string) => {
      const n = Number.parseInt(v.trim(), 10);
      return Number.isNaN(n) ? undefined : n;
    };
    return {
      lojaId: lojaId as number,
      requisicaoId: numero(f.requisicao),
      dataInicio: texto(f.dataInicio),
      dataFim: texto(f.dataFim),
      codProduto: texto(f.codProduto),
      nomeProduto: texto(f.nomeProduto),
      codCategoria: numero(f.codCategoria),
      nomeCategoria: texto(f.nomeCategoria),
      prioridade: texto(f.prioridade),
      loja: texto(f.loja),
      solicitante: texto(f.solicitante),
      cnpjFornecedor: texto(f.cnpjFornecedor),
      nomeFornecedor: texto(f.nomeFornecedor),
      ordemCompra: numero(f.ordemCompra),
      comprador: texto(f.comprador),
    };
  };

  const handleSave = async () => {
    if (modoSelecao === 'todos') {
      // Linhas visíveis que o usuário editou à mão. Vão como `overrides`,
      // aplicados por cima do default do servidor; o que ele não tocou fica com o
      // default — que nesta tela é o MESMO número que a coluna já exibe.
      const overrides = items
        .filter(
          (it) =>
            !excludeIds.has(it.ordemCompraRomaneioItemId) &&
            quantities[it.ordemCompraRomaneioItemId] !== undefined,
        )
        .map((it) => ({
          ordemCompraItemId: it.ordemCompraRomaneioItemId,
          qtdRecebida: quantities[it.ordemCompraRomaneioItemId],
        }));

      // O backend recusa override com quantidade não-positiva já na resolução, e
      // a ação é tudo-ou-nada. Barrar aqui é síncrono e nomeia o problema; deixar
      // passar derrubaria o lote inteiro por uma linha zerada.
      if (overrides.some((o) => o.qtdRecebida <= 0)) {
        await showAlert(AVISO_QTD_ZERO, { variant: 'warning' });
        return;
      }

      onSelecionarTudo?.({
        filtro: montarFiltro(),
        excludeIds: [...excludeIds],
        overrides,
        totalEstimado: qtdSelecionada,
      });
      onClose();
      return;
    }

    if (selectedProducts.length === 0) {
      await showAlert('Selecione pelo menos um item.', { variant: 'warning' });
      return;
    }
    const saved: ReceivedItem[] = selectedProducts.map(item => ({
      ...item,
      qtdRecebida: quantities[item.ordemCompraRomaneioItemId] ?? item.qtdRecebida,
    }));
    onItemsAdded(saved);
    onClose();
  };

  useEffect(() => setIsLoading(fetchLoading), [fetchLoading]);
  useEffect(() => reset(), []);

  const columns = [
    { header: 'Código', render: (i: PendingReceiptItem) => i.codProduto },
    { header: 'Cód. Categoria', render: (i: PendingReceiptItem) => i.codCategoriaProduto },
    { header: 'Nome do Produto', render: (i: PendingReceiptItem) => i.nomeProduto },
    { header: 'Categoria', render: (i: PendingReceiptItem) => i.nomeCategoria },
    { header: 'Qtd. Total', render: (i: PendingReceiptItem) => i.qtdCompraTransferenciaOriginal },
    { header: 'Ordem de Compra', render: (i: PendingReceiptItem) => i.ordemCompraRomaneioId },
    { header: 'Fornecedor', render: (i: PendingReceiptItem) => i.nomeFornecedor },
    { header: 'Status Recebimento', render: (i: PendingReceiptItem) => i.statusRecebimento },
    { header: 'Qtd. Receber', render: (i: PendingReceiptItem) => i.qtdReceber },
    { header: 'Qtd. Total Recebido', render: (i: PendingReceiptItem) => i.qtdRecebidaTotal },
    {
      header: 'Qtd. Recebida',
      render: (i: PendingReceiptItem) => (
        <input
          type="number"
          min="0.001"
          max={i.qtdReceber}
          step="0.001"
          value={quantities[i.ordemCompraRomaneioItemId] ?? i.qtdRecebida}
          onChange={e => handleQuantityChange(i.ordemCompraRomaneioItemId, e.target.value, i.qtdReceber)}
          className="quantity-input"
        />
      ),
    },
  ];

  return (
    <div className={`search-pending-receipt-product-modal-content ${theme}`}>
      <div className="header-container">
        <h2>
          <FontAwesomeIcon icon={faMagnifyingGlass} /> Pesquisar Produtos Pendente de Recebimento
        </h2>
        <label className="toggle-switch">
          <input
            type="checkbox"
            checked={filterMode === 'Tudo'}
            onChange={handleToggleFilterMode}
          />
          <span className="slider">
            <span className="slider-text">{filterMode}</span>
          </span>
        </label>
      </div>
      <div className="search-form">
        {filterMode === 'Específico' && (
          <div className="form-inputs">
            <div className="form-row">
              <input
                type="text"
                value={requisicao}
                onChange={(e) => setRequisicao(e.target.value)}
                placeholder="Requisição"
                className="search-input"
              />
              <DateRangeInput
                dataInicio={dataInicio}
                setDataInicio={setDataInicio}
                dataFim={dataFim}
                setDataFim={setDataFim}
                theme={theme}
                label="Data entrega"
              />
              <input
                type="text"
                value={codProduto}
                onChange={(e) => setCodProduto(e.target.value)}
                placeholder="Código"
                className="search-input"
              />
              <input
                type="text"
                value={nomeProduto}
                onChange={(e) => setNomeProduto(e.target.value)}
                placeholder="Nome do Produto"
                className="search-input"
              />
            </div>
            <div className="form-row">
              <input
                type="text"
                value={codCategoria}
                onChange={(e) => setCodCategoria(e.target.value)}
                placeholder="Código Categoria"
                className="search-input"
              />
              <input
                type="text"
                value={nomeCategoria}
                onChange={(e) => setNomeCategoria(e.target.value)}
                placeholder="Categoria"
                className="search-input"
              />
              <input
                type="text"
                value={prioridade}
                onChange={(e) => setPrioridade(e.target.value)}
                placeholder="Prioridade"
                className="search-input"
              />
              <input
                type="text"
                value={loja}
                onChange={(e) => setLoja(e.target.value)}
                placeholder="Loja Solicitante"
                className="search-input"
              />
              <input
                type="text"
                value={solicitante}
                onChange={(e) => setSolicitante(e.target.value)}
                placeholder="Solicitante"
                className="search-input"
              />
            </div>
            <div className="form-row">
              <input
                type="text"
                value={cnpjFornecedor}
                onChange={(e) => setCnpjFornecedor(e.target.value)}
                placeholder="CNPJ Fornecedor"
                className="search-input"
              />
              <input
                type="text"
                value={nomeFornecedor}
                onChange={(e) => setNomeFornecedor(e.target.value)}
                placeholder="Nome Fornecedor"
                className="search-input"
              />
              <input
                type="text"
                value={ordemCompra}
                onChange={(e) => setOrdemCompra(e.target.value)}
                placeholder="Ordem de Compra"
                className="search-input"
              />
              <input
                type="text"
                value={comprador}
                onChange={(e) => setComprador(e.target.value)}
                placeholder="Comprador"
                className="search-input"
              />
            </div>
          </div>
        )}
        <SearchButton isLoading={isLoading} onClick={handleSearch}>
          Procurar
        </SearchButton>
      </div>
        <ScrollableTable<PendingReceiptItem>
          data={items}
          hasMore={hasMore}
          loadMore={loadMore}
          isLoading={isLoading}
          columns={columns}
          onSelectChange={handleSelectChange}
          emptyMessage="Nenhum produto encontrado."
          showCheckboxes={true}
          singleSelect={false}
          isModal={true}
          // Opt-in. Ausente enquanto o filtro da busca não permitir o selectAll —
          // e prop ausente é, por construção, o caminho histórico do
          // ScrollableTable: o cabeçalho volta a marcar só a página carregada,
          // que é o que o usuário sempre teve aqui.
          selecaoEntrePaginas={
            podeSelecionarTudo
              ? {
                  estadoCabecalho,
                  estaSelecionado: (it: PendingReceiptItem) =>
                    modoSelecao === 'todos'
                      ? !excludeIds.has(it.ordemCompraRomaneioItemId)
                      : selectedProducts.some(
                          (s) => s.ordemCompraRomaneioItemId === it.ordemCompraRomaneioItemId,
                        ),
                  onToggleCabecalho: handleToggleCabecalho,
                  onSelecionarItem: handleSelecionarItem,
                }
              : undefined
          }
        />
      <div className="form-actions">
        {/* Escopo da seleção. Informativo — o gesto é o checkbox do cabeçalho. */}
        {modoSelecao === 'todos' && (
          <span className="pendentes-info-selecao">
            {textoSelecao(qtdSelecionada, totalItems)}
          </span>
        )}
        {/* Por que "selecionar tudo" não está disponível. Só aparece quando o pai
            sabe consumi-lo — sem isso seria uma promessa que a tela não cumpre. */}
        {onSelecionarTudo && !podeSelecionarTudo && (
          <span className="pendentes-aviso-selecao">
            {motivoSelecaoTudoIndisponivel ??
              'Para selecionar todos entre páginas, busque no modo Específico filtrando por fornecedor.'}
          </span>
        )}
        <SaveButton
          isLoading={false}
          onClick={handleSave}
          disabled={!temSelecao}
        />
      </div>
    </div>
  );
};

export default SearchPendingReceiptProductModalContent;
